import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { apiHelpers } from '@/lib/api';
import { getErrorMessage } from '@/lib/errors';
import { useAuthStore, isGhostSession } from '@/stores/auth';
import { formatDate, getStatusColor, copyToClipboard, getProjectUrl } from '@/lib/utils';
import { can } from '@/types';
import type { Project } from '@/types';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Search,
  FolderOpen,
  Copy,
  MoreVertical,
  Settings,
  Check,
  X,
  Loader2,
  Pencil,
  ShoppingCart,
  Puzzle,
  Trash2,
  Globe,
  Cloud,
} from 'lucide-react';

export default function Projects() {
  const [search, setSearch] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [renameProject, setRenameProject] = useState<Project | null>(null);
  const [deleteProject, setDeleteProject] = useState<Project | null>(null);
  const { user } = useAuthStore();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['projects', search],
    queryFn: () => apiHelpers.getProjects({ search, limit: 50 }),
  });

  const projects = data?.data?.data || [];

  // Internal-only CF Worker link visibility:
  //   - SUPER_ADMIN viewing their own console
  //   - any user inside a ghost-login session (super-admin impersonating a customer)
  // Customers should never see the workers.dev URL; it's an internal endpoint
  // even though the URL itself isn't a secret.
  const showWorkerLink = user?.role === 'SUPER_ADMIN' || isGhostSession();

  const handleCopyApiKey = async (apiKey: string, projectId: string) => {
    await copyToClipboard(apiKey);
    setCopiedId(projectId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Projects</h1>
          <p className="text-muted-foreground">
            Manage your website projects and their configurations
          </p>
        </div>
        {/* Projects are auto-created with teams — no manual creation */}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
        <input
          type="text"
          placeholder="Search projects..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="bg-background focus:ring-primary w-full rounded-lg border py-2 pl-10 pr-4 focus:border-transparent focus:outline-none focus:ring-2"
        />
      </div>

      {/* Projects Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
        </div>
      ) : error ? (
        <div className="py-12 text-center">
          <p className="text-red-500">Failed to load projects</p>
        </div>
      ) : projects.length === 0 ? (
        <div className="bg-card rounded-xl border py-12 text-center">
          <FolderOpen className="text-muted-foreground mx-auto mb-4 h-12 w-12" />
          <h3 className="mb-2 font-semibold">No projects found</h3>
          <p className="text-muted-foreground mb-4">
            {search ? 'Try a different search term' : 'Create your first project to get started'}
          </p>
          {/* Projects are auto-created with teams — no manual creation */}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {projects.map((project: Project) => (
            <div
              key={project.id}
              className="bg-card overflow-hidden rounded-xl border transition-shadow hover:shadow-md"
            >
              <div className="p-6">
                <div className="mb-4 flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-primary/10 flex h-10 w-10 items-center justify-center rounded-lg">
                      <FolderOpen className="text-primary h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{project.name}</h3>
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs ${getStatusColor(project.status)}`}
                      >
                        {project.status}
                      </span>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="hover:bg-muted rounded p-1">
                        <MoreVertical className="text-muted-foreground h-4 w-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem onClick={() => setRenameProject(project)}>
                        <Pencil className="mr-2 h-4 w-4" />
                        Rename
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => navigate(`/projects/${project.id}?tab=domains`)}
                      >
                        <Globe className="mr-2 h-4 w-4" />
                        Domains
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => navigate(`/projects/${project.id}/ecommerce`)}
                      >
                        <ShoppingCart className="mr-2 h-4 w-4" />
                        E-Commerce
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => navigate(`/projects/${project.id}?tab=plugins`)}
                      >
                        <Puzzle className="mr-2 h-4 w-4" />
                        Plugins
                      </DropdownMenuItem>
                      {can(user, 'delete_project') && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => setDeleteProject(project)}
                            className="text-red-600 focus:text-red-600"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete Project
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {project.description && (
                  <p className="text-muted-foreground mb-4 line-clamp-2 text-sm">
                    {project.description}
                  </p>
                )}

                {/* Public-facing site URL.
                    Priority:
                      1. Custom domain (first entry in project.domains, or legacy project.domain).
                      2. {slug}.sites.tyashin.com — every project's free Tyashin subdomain.
                      3. Legacy getProjectUrl() (handles localDevUrl in dev).
                    Always a hyperlink — never plain text. */}
                {(() => {
                  const customDomain = project.domains?.[0] || project.domain;
                  const publicUrl = customDomain
                    ? customDomain.startsWith('http')
                      ? customDomain
                      : `https://${customDomain}`
                    : project.sitesUrl || getProjectUrl(project);
                  const displayLabel =
                    customDomain || project.sitesUrl?.replace(/^https?:\/\//, '');
                  return publicUrl ? (
                    <a
                      href={publicUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary mb-2 inline-flex items-center gap-1 text-sm hover:underline"
                    >
                      <Globe className="h-3 w-3" />
                      {displayLabel || 'Open site'}
                    </a>
                  ) : null;
                })()}

                {/* Internal CF Worker URL — SUPER_ADMIN + ghost-login only. */}
                {showWorkerLink && project.workerUrl && (
                  <a
                    href={project.workerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-primary mb-4 inline-flex items-center gap-1 text-xs hover:underline"
                    title={`Cloudflare Worker: ${project.workerScriptName}`}
                  >
                    <Cloud className="h-3 w-3" />
                    {project.workerScriptName}.workers.dev
                  </a>
                )}

                {/* API Key */}
                <div className="bg-muted/50 mb-4 rounded-lg p-3">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-muted-foreground text-xs font-medium">API Key</span>
                    {project.apiKey ? (
                      <button
                        onClick={() => handleCopyApiKey(project.apiKey!, project.id)}
                        className="text-primary flex items-center gap-1 text-xs hover:underline"
                      >
                        {copiedId === project.id ? (
                          <>
                            <Check className="h-3 w-3" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="h-3 w-3" />
                            Copy
                          </>
                        )}
                      </button>
                    ) : (
                      <span className="text-muted-foreground text-xs">Regenerate in settings</span>
                    )}
                  </div>
                  <code className="text-muted-foreground block truncate font-mono text-xs">
                    {project.apiKey || project.apiKeyPrefix || 'No API key'}
                  </code>
                </div>

                {/* Plugins count — server-computed from pluginConfigs collection.
                    `installedPlugins` is a legacy field; `pluginCount` is the truth. */}
                <div className="text-muted-foreground flex items-center gap-4 text-sm">
                  <span>
                    {project.pluginCount ?? project.installedPlugins?.length ?? 0} plugins
                  </span>
                  <span>Created {formatDate(project.createdAt)}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="bg-muted/30 flex items-center gap-2 border-t px-6 py-3">
                <Link
                  to={`/projects/${project.id}`}
                  className="text-primary hover:bg-primary/10 flex-1 rounded py-1.5 text-center text-sm font-medium transition-colors"
                >
                  <Settings className="mr-1 inline-block h-4 w-4" />
                  Manage
                </Link>
                <Link
                  to={`/projects/${project.id}/newsletter`}
                  className="text-muted-foreground hover:bg-muted flex-1 rounded py-1.5 text-center text-sm font-medium transition-colors"
                >
                  Newsletter
                </Link>
                <Link
                  to={`/projects/${project.id}/contacts`}
                  className="text-muted-foreground hover:bg-muted flex-1 rounded py-1.5 text-center text-sm font-medium transition-colors"
                >
                  Contacts
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Rename Modal */}
      {renameProject && (
        <RenameProjectModal project={renameProject} onClose={() => setRenameProject(null)} />
      )}

      {/* Delete Confirmation Modal */}
      {deleteProject && (
        <DeleteProjectModal project={deleteProject} onClose={() => setDeleteProject(null)} />
      )}
    </div>
  );
}

// ── Rename Project Modal ────────────────────────────────────────────────────

function RenameProjectModal({ project, onClose }: { project: Project; onClose: () => void }) {
  const [name, setName] = useState(project.name);
  const [error, setError] = useState('');
  const queryClient = useQueryClient();

  const renameMutation = useMutation({
    mutationFn: () => apiHelpers.updateProject(project.id, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      onClose();
    },
    onError: (err) => setError(getErrorMessage(err, 'Failed to rename project')),
  });

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-card w-full max-w-sm rounded-xl border shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b p-5">
            <h2 className="text-lg font-semibold">Rename Project</h2>
            <button onClick={onClose} className="hover:bg-muted rounded p-1">
              <X className="h-5 w-5" />
            </button>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (name.trim() && name !== project.name) renameMutation.mutate();
            }}
            className="space-y-4 p-5"
          >
            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-800 dark:bg-red-900/20">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <label className="text-sm font-medium">Project Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                required
                className="bg-background focus:ring-primary w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="hover:bg-muted flex-1 rounded-lg border px-4 py-2 font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={renameMutation.isPending || !name.trim() || name === project.name}
                className="bg-primary text-primary-foreground hover:bg-primary/90 flex flex-1 items-center justify-center rounded-lg px-4 py-2 font-medium disabled:opacity-50"
              >
                {renameMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

// ── Delete Project Modal ────────────────────────────────────────────────────

function DeleteProjectModal({ project, onClose }: { project: Project; onClose: () => void }) {
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState('');
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: () => apiHelpers.deleteProject(project.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      onClose();
    },
    onError: (err) => setError(getErrorMessage(err, 'Failed to delete project')),
  });

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-card w-full max-w-sm rounded-xl border shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b p-5">
            <h2 className="text-lg font-semibold text-red-600">Delete Project</h2>
            <button onClick={onClose} className="hover:bg-muted rounded p-1">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="space-y-4 p-5">
            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-800 dark:bg-red-900/20">
                {error}
              </div>
            )}
            <p className="text-sm">
              This will permanently delete <strong>{project.name}</strong> and all its data
              (newsletter subscribers, contacts, generated websites, plugins).
            </p>
            <p className="text-muted-foreground text-sm">
              Type <strong>delete {project.name}</strong> to confirm:
            </p>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={`delete ${project.name}`}
              className="bg-background w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
            />
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="hover:bg-muted flex-1 rounded-lg border px-4 py-2 font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending || confirmText !== `delete ${project.name}`}
                className="flex flex-1 items-center justify-center rounded-lg bg-red-600 px-4 py-2 font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Delete Forever'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// CreateProjectModal removed — projects are auto-created with teams (1:1 relationship)
